import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Filter, Download, Plus, Mail, Phone, Building2, Calendar, CheckCircle2, Users, Trash2, AlertCircle, Bell, Clock, AlertTriangle, Info, RefreshCw, ListTodo, BarChart3, X, UserPlus, Dog, PenLine, ArrowUpDown, ArrowUp, ArrowDown, LayoutGrid, List as ListIcon } from "lucide-react";
import { formatPhoneNumber } from "@/utils/formatting";
import ServiceRibbon from "@/components/ServiceRibbon";
import { LeadsKanban } from "@/components/LeadsKanban";
import SelfRegisteredBadge from "@/components/SelfRegisteredBadge";
import { ProfileTags } from "@/components/smartleads/ProfileTags";
import { LeadStatusRulesInfo } from "@/components/leads/LeadStatusRules";
import { useLeadTags } from "@/components/leads/useLeadTags";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { LeadsBulkDelete, BULK_DELETE_ROLES } from "@/components/leads/LeadsBulkDelete";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Leads() {
  const [leads, setLeads] = useState([]);
  const [completedLeads, setCompletedLeads] = useState([]);
  const [posMatches, setPosMatches] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "open"); // Get tab from URL or default to open
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('leads_view_mode') || 'list'); // 'list' | 'kanban'
  const [urgencyFilter, setUrgencyFilter] = useState("all"); // New: urgency filter
  const [locationFilter, setLocationFilter] = useState("all"); // New: location filter for master admins
  const [serviceFilter, setServiceFilter] = useState(null); // New: service filter
  const [locations, setLocations] = useState([]); // Available locations
  const [currentUser, setCurrentUser] = useState(null); // Current user info
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const canBulkDelete = BULK_DELETE_ROLES.includes(currentUser?.role);
  const [dateRangeStats, setDateRangeStats] = useState(null); // Date range statistics
  const [startDate, setStartDate] = useState(''); // Start date for filter
  const [endDate, setEndDate] = useState(''); // End date for filter
  const [showDateStats, setShowDateStats] = useState(false); // Show date range stats
  const [followUpStats, setFollowUpStats] = useState(null); // Follow-up statistics
  const [syncing, setSyncing] = useState(false); // Gmail sync state
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showAddLead, setShowAddLead] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({ name: '', email: '', phone: '', service: '', dog_name: '', dog_breed: '', notes: '', source: 'event' });
  const [addingLead, setAddingLead] = useState(false);
  // Sorting state
  const [sortBy, setSortBy] = useState("default");
  const [sortOrder, setSortOrder] = useState("desc");
  // Contact-count filter ("all" | "none" | "3plus")
  const [contactedFilter, setContactedFilter] = useState(() => localStorage.getItem("leads_contacted_filter") || "all");
  // Rep Activity state
  const [repActivityStats, setRepActivityStats] = useState(null);
  const [repActivityLoading, setRepActivityLoading] = useState(false);
  const [selectedRepFilter, setSelectedRepFilter] = useState(null);
  const [repStartDate, setRepStartDate] = useState(() => {
    // Default to 7 days ago
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [repEndDate, setRepEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const navigate = useNavigate();

  // Auto-refresh when tab becomes visible (user returns after being idle)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if it's been more than 5 minutes since last refresh
        const timeSinceRefresh = (new Date() - lastRefresh) / 1000 / 60;
        if (timeSinceRefresh > 5) {
          console.log('Tab visible after idle - refreshing leads data');
          fetchLeads();
          fetchFollowUpStats();
          setLastRefresh(new Date());
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [lastRefresh, statusFilter, sourceFilter, locationFilter]);

  // Auto-refresh every 10 minutes while tab is active
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        console.log('Auto-refreshing leads data (10 min interval)');
        fetchLeads();
        fetchFollowUpStats();
        setLastRefresh(new Date());
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(interval);
  }, [statusFilter, sourceFilter, locationFilter]);

  const [currentLocId] = useState(() => localStorage.getItem('currentLocationId') || '');
  
  useEffect(() => {
    // SWR: show cached leads immediately
    try {
      const cached = localStorage.getItem(`swr_leads_${currentLocId}`);
      if (cached) { setLeads(JSON.parse(cached)); setLoading(false); }
    } catch(e) {}
    fetchCurrentUser();
    fetchLocations();
    fetchFollowUpStats();
  }, [currentLocId]);

  useEffect(() => {
    fetchLeads();
    fetchCompletedLeads();
  }, [statusFilter, sourceFilter, locationFilter]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('leads_view_mode', mode);
  };

  // Kanban drag → persist new stage. Optimistic move, revert on failure so the
  // card only stays in the dragged column once the backend confirms.
  const updateLeadStatus = async (leadId, newStatus) => {
    const prev = leads;
    setLeads((curr) => curr.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status: newStatus });
      toast.success(`Moved to ${newStatus.replace(/_/g, ' ')}`);
      fetchLeads();
      fetchCompletedLeads();
    } catch (error) {
      setLeads(prev); // revert — card snaps back to its original column
      toast.error('Failed to move lead');
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error("Failed to fetch current user");
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch locations");
    }
  };

  const fetchFollowUpStats = async () => {
    try {
      const locId = localStorage.getItem('currentLocationId');
      const locParam = locId ? `?location_id=${encodeURIComponent(locId)}` : '';
      const response = await axios.get(`${API}/leads/stats/follow-up${locParam}`);
      setFollowUpStats(response.data);
    } catch (error) {
      console.error("Failed to fetch follow-up stats");
    }
  };

  const fetchRepActivity = async () => {
    if (!repStartDate || !repEndDate) {
      toast.error("Please select start and end dates");
      return;
    }
    setRepActivityLoading(true);
    try {
      const response = await axios.get(`${API}/leads/stats/rep-activity`, {
        params: { start_date: repStartDate, end_date: repEndDate }
      });
      setRepActivityStats(response.data);
    } catch (error) {
      console.error("Failed to fetch rep activity:", error);
      toast.error("Failed to load rep activity data");
    } finally {
      setRepActivityLoading(false);
    }
  };

  const handleSyncEmails = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/gmail/sync-emails`);
      toast.success(response.data.message);
      // Refresh leads after sync
      fetchLeads();
      fetchCompletedLeads();
      fetchFollowUpStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync emails");
    } finally {
      setSyncing(false);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    if (!addLeadForm.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!addLeadForm.email?.trim() && !addLeadForm.phone?.trim()) {
      toast.error("Email or phone is required");
      return;
    }
    setAddingLead(true);
    try {
      await axios.post(`${API}/leads`, {
        name: addLeadForm.name.trim(),
        email: addLeadForm.email.trim() || null,
        phone: addLeadForm.phone.trim() || null,
        source: addLeadForm.source || 'event',
        service: addLeadForm.service || null,
        notes: addLeadForm.notes.trim() || null,
        dog_name: addLeadForm.dog_name.trim() || null,
        dog_breed: addLeadForm.dog_breed.trim() || null,
      });
      toast.success(`Lead "${addLeadForm.name}" added successfully`);
      setShowAddLead(false);
      setAddLeadForm({ name: '', email: '', phone: '', service: '', dog_name: '', dog_breed: '', notes: '', source: 'event' });
      fetchLeads();
      fetchFollowUpStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add lead");
    } finally {
      setAddingLead(false);
    }
  };


  const fetchDateRangeStats = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    try {
      const response = await axios.get(`${API}/leads/stats/date-range`, {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      setDateRangeStats(response.data);
      setShowDateStats(true);
      toast.success(`Showing leads from ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch date range statistics");
    }
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setDateRangeStats(null);
    setShowDateStats(false);
    fetchLeads();
    toast.success("Date filter cleared");
  };

  const fetchLeads = async () => {
    try {
      const locId = localStorage.getItem('currentLocationId');
      const params = new URLSearchParams();
      if (locId) params.append("location_id", locId);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (locationFilter !== "all") params.append("location_filter", locationFilter);
      params.append("_t", Date.now());
      
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      setLeads(response.data);
      // SWR: cache for instant display on next visit
      try { localStorage.setItem(`swr_leads_${currentLocId}`, JSON.stringify(response.data)); } catch(e) {}
    } catch (error) {
      if (leads.length === 0 && error.response?.status !== 401 && error.response?.status !== 403) {
        console.error("[LEADS] fetch failed:", error.response?.status);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads?completed_only=true&_t=${Date.now()}`);
      const leadsData = response.data;
      setCompletedLeads(leadsData);
      
      // POS matches are now embedded in each lead by the backend (pos_match field)
      // No need for N individual API calls — extract directly from the response
      const posMatchesData = {};
      for (const lead of leadsData) {
        if (lead.pos_match) {
          posMatchesData[lead.id] = lead.pos_match;
        }
      }
      setPosMatches(posMatchesData);
    } catch (error) {
      console.error("Failed to load completed leads");
    }
  };

  const handleDeleteLead = async (leadId, leadName, event) => {
    // Stop event propagation so clicking delete doesn't navigate to lead detail
    event.stopPropagation();
    
    if (window.confirm(`Are you sure you want to delete ${leadName}? This cannot be undone.`)) {
      try {
        await axios.delete(`${API}/leads/${leadId}`);
        toast.success("Lead deleted successfully");
        // Refresh both lists
        fetchLeads();
        fetchCompletedLeads();
      } catch (error) {
        toast.error("Failed to delete lead");
      }
    }
  };

  // Determine which leads to show based on active tab
  // If date range filter is active, use those leads
  let leadsToShow = dateRangeStats ? dateRangeStats.leads : leads;
  
  if (!dateRangeStats) {
    if (activeTab === "completed") {
      leadsToShow = completedLeads;
    } else if (activeTab === "open") {
      leadsToShow = leads.filter(l => ['new', 'contacted'].includes(l.status));
    } else if (activeTab === "existing") {
      leadsToShow = leads.filter(l => l.status === 'existing_customer');
    } else if (activeTab === "qualified") {
      leadsToShow = leads.filter(l => l.status === 'qualified');
    } else if (activeTab === "lost") {
      leadsToShow = leads.filter(l => ['lost', 'not_a_lead', 'closed_no_response'].includes(l.status));
    }
  }

  const filteredLeads = leadsToShow.filter((lead) => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = (
        lead.name?.toLowerCase().includes(search) ||
        lead.canonical_email?.toLowerCase().includes(search) ||
        lead.company?.toLowerCase().includes(search)
      );
      if (!matchesSearch) return false;
    }
    
    // Status filter
    if (statusFilter && statusFilter !== "all") {
      if (lead.status !== statusFilter) return false;
    }
    
    // Source filter
    if (sourceFilter && sourceFilter !== "all") {
      if (lead.source !== sourceFilter) return false;
    }
    
    // Service filter (from KPI cards)
    if (serviceFilter) {
      const leadService = lead.service || 'No Service';
      if (leadService !== serviceFilter) return false;
    }
    
    // Urgency filter (only apply on open tab)
    if (activeTab === "open" && urgencyFilter !== "all") {
      // With new logic, ALL open leads have follow_up
      // Filter by the selected urgency
      if (lead.follow_up && lead.follow_up.urgency !== urgencyFilter) {
        return false;
      }
      // If somehow lead has no follow_up data and filter is active, hide it
      if (!lead.follow_up) {
        return false;
      }
    }

    // Contact count filter
    if (contactedFilter === "3plus" && (lead.outreach_count ?? 0) < 3) return false;
    if (contactedFilter === "none" && (lead.outreach_count ?? 0) !== 0) return false;

    return true;
  });

  // Sort leads — user-selected sort overrides tab defaults
  const sortedLeads = [...filteredLeads].sort((a, b) => {
    // User-selected sort
    if (sortBy !== "default") {
      const dir = sortOrder === "asc" ? 1 : -1;
      if (sortBy === "activity_date") {
        const getLatestActivity = (lead) => {
          const log = lead.action_log || [];
          if (log.length === 0) return new Date(0);
          return new Date(Math.max(...log.map(a => new Date(a.date).getTime())));
        };
        return dir * (getLatestActivity(a) - getLatestActivity(b));
      }
      if (sortBy === "created_date") {
        return dir * (new Date(a.created_at) - new Date(b.created_at));
      }
      if (sortBy === "score") {
        return dir * ((a.score || 0) - (b.score || 0));
      }
      if (sortBy === "revenue") {
        const revA = parseFloat(posMatches[a.id]?.pos_payload?.total_revenue || a.revenue || 0);
        const revB = parseFloat(posMatches[b.id]?.pos_payload?.total_revenue || b.revenue || 0);
        return dir * (revA - revB);
      }
      if (sortBy === "name") {
        return dir * (a.name || "").localeCompare(b.name || "");
      }
      if (sortBy === "age") {
        // Newest first when desc; oldest first when asc. Age uses created_at.
        return dir * (new Date(b.created_at) - new Date(a.created_at));
      }
      if (sortBy === "contacts") {
        const ca = a.outreach_count ?? 0;
        const cb = b.outreach_count ?? 0;
        return dir * (cb - ca);
      }
      return 0;
    }

    // Default sort: self-registered leads first, then created_at DESC (newest first)
    const aReg = a.self_registered ? 0 : 1;
    const bReg = b.self_registered ? 0 : 1;
    if (aReg !== bReg) return aReg - bReg;
    return new Date(b.created_at) - new Date(a.created_at);
  });
  const leadTags = useLeadTags(sortedLeads);

  const exportToCSV = () => {
    if (sortedLeads.length === 0) {
      toast.error("No leads to export");
      return;
    }

    const headers = ["ID", "Name", "Email", "Phone", "Company", "Status", "Score", "Source", "Created At"];
    const rows = sortedLeads.map((lead) => [
      lead.id,
      lead.name || "",
      lead.canonical_email || "",
      lead.primary_phone || "",
      lead.company || "",
      lead.status,
      lead.score,
      lead.source,
      new Date(lead.created_at).toLocaleDateString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Leads exported successfully!");
  };

  const handleServiceClick = (service) => {
    // Toggle filter: if same service clicked, clear filter; otherwise set new filter
    if (serviceFilter === service) {
      setServiceFilter(null);
      toast.info("Service filter cleared");
    } else {
      setServiceFilter(service);
      toast.success(`Filtering by: ${service}`);
    }
  };

  const getUrgencyBadge = (followUp) => {
    if (!followUp) return null;

    const configs = {
      urgent: {
        gradient: 'bg-gradient-to-r from-red-500 to-rose-500',
        icon: Bell,
        label: 'URGENT'
      },
      warning: {
        gradient: 'bg-gradient-to-r from-amber-500 to-orange-500',
        icon: AlertCircle,
        label: 'WARNING'
      },
      normal: {
        gradient: 'bg-gradient-to-r from-blue-500 to-indigo-500',
        icon: Clock,
        label: 'FOLLOW-UP'
      }
    };

    const config = configs[followUp.urgency];
    if (!config) return null;

    const Icon = config.icon;
    
    return (
      <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm ${config.gradient}`}>
        <Icon className="h-3.5 w-3.5" />
        {config.label}
        <span className="ml-1 opacity-90">({followUp.days_since_activity}d)</span>
      </div>
    );
  };

  // Get last action from action_log
  const getLastAction = (lead) => {
    const actionLog = lead.action_log || [];
    if (actionLog.length === 0) return null;
    
    // Sort by date descending and get the most recent
    const sorted = [...actionLog].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted[0];
  };

  // Format last action for display
  const formatLastAction = (action) => {
    if (!action) return { text: 'No activity', icon: Clock, color: 'text-gray-400' };
    
    const actionDate = new Date(action.date);
    const now = new Date();
    const diffMs = now - actionDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    let timeAgo;
    if (diffDays > 30) {
      timeAgo = `${Math.floor(diffDays / 30)}mo ago`;
    } else if (diffDays > 0) {
      timeAgo = `${diffDays}d ago`;
    } else if (diffHours > 0) {
      timeAgo = `${diffHours}h ago`;
    } else {
      timeAgo = 'Just now';
    }
    
    const actionTypes = {
      call: { icon: Phone, label: 'Called', color: 'text-blue-600' },
      email: { icon: Mail, label: 'Emailed', color: 'text-green-600' },
      comment: { icon: Info, label: 'Note', color: 'text-gray-600' },
      status_change: { icon: CheckCircle2, label: 'Status', color: 'text-purple-600' },
    };
    
    const config = actionTypes[action.type] || { icon: Clock, label: action.type || 'Action', color: 'text-gray-600' };
    
    return {
      text: `${config.label} ${timeAgo}`,
      icon: config.icon,
      color: config.color,
      by: action.user_name || action.user_email?.split('@')[0] || '',
      isRecent: diffDays <= 3,
      isStale: diffDays > 7,
    };
  };

  return (
    <div className="ops-v3 space-y-6" data-testid="leads-page">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl sm:text-[32px] font-extrabold tracking-tight text-gray-900 mb-1" style={{ fontFamily: 'Manrope' }}>Leads</h1>
        <p className="text-sm sm:text-base text-gray-500">Manage and track all your incoming leads</p>
      </div>
      <LeadStatusRulesInfo />

      {/* Service Ribbon - shows counts for currently filtered leads */}
      <ServiceRibbon 
        leads={leadsToShow} 
        onServiceClick={handleServiceClick}
        selectedService={serviceFilter}
      />

      {/* Follow-Up Status Section - Clickable to filter by urgency */}
      {followUpStats && (
        <Card className={`card border-l-4 ${followUpStats.total_needs_followup > 0 ? 'border-l-orange-500' : 'border-l-green-500'}`}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-[10px] grid place-items-center shrink-0" style={{ background: "#E6F6F3" }}><Bell className="h-[18px] w-[18px]" style={{ color: "#1F8A7C" }} strokeWidth={2.25} /></span>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900 leading-tight" style={{ fontFamily: 'Manrope' }}>
                    Follow-Up Status
                  </h3>
                  <p className="text-sm text-gray-500">
                    {followUpStats.total_needs_followup > 0 
                      ? `${followUpStats.total_needs_followup} lead${followUpStats.total_needs_followup !== 1 ? 's' : ''} need${followUpStats.total_needs_followup === 1 ? 's' : ''} attention - click to filter`
                      : '✅ No follow-ups required - all caught up!'
                    }
                  </p>
                </div>
              </div>
            </div>

            {followUpStats.total_needs_followup > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Urgent - Modern gradient style */}
                <button
                  onClick={() => setUrgencyFilter(urgencyFilter === 'urgent' ? 'all' : 'urgent')}
                  className={`rounded-xl border border-gray-200 bg-white text-gray-900 transition-colors ${
                    urgencyFilter === 'urgent' ? 'ring-2 ring-[#1F8A7C]/40' : 'hover:border-[#1F8A7C]/40'
                  } v3-tint-neg p-5`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <p className="text-gray-500 text-sm font-medium">Urgent</p>
                      <p className="text-4xl v3-num font-bold mt-1">{followUpStats.urgent}</p>
                    </div>
                    <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center">
                      <AlertCircle className="h-6 w-6" />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2 text-left">No contact yet</p>
                </button>

                {/* Warning - Modern gradient style */}
                <button
                  onClick={() => setUrgencyFilter(urgencyFilter === 'warning' ? 'all' : 'warning')}
                  className={`rounded-xl border border-gray-200 bg-white text-gray-900 transition-colors ${
                    urgencyFilter === 'warning' ? 'ring-2 ring-[#1F8A7C]/40' : 'hover:border-[#1F8A7C]/40'
                  } v3-tint-warn p-5`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <p className="text-gray-500 text-sm font-medium">Warning</p>
                      <p className="text-4xl v3-num font-bold mt-1">{followUpStats.warning}</p>
                    </div>
                    <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center">
                      <AlertTriangle className="h-6 w-6" />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2 text-left">2+ days ago</p>
                </button>

                {/* Queue - Modern gradient style */}
                <button
                  onClick={() => setUrgencyFilter(urgencyFilter === 'queue' ? 'all' : 'queue')}
                  className={`rounded-xl border border-gray-200 bg-white text-gray-900 transition-colors ${
                    urgencyFilter === 'queue' ? 'ring-2 ring-[#1F8A7C]/40' : 'hover:border-[#1F8A7C]/40'
                  } v3-tint-pos p-5`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <p className="text-gray-500 text-sm font-medium">Queue</p>
                      <p className="text-4xl v3-num font-bold mt-1">{followUpStats.queue || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center">
                      <Info className="h-6 w-6" />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2 text-left">Recent contact</p>
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center p-8 bg-green-50 rounded-lg border border-green-200">
                <div className="text-center">
                  <div className="text-5xl mb-3">🎉</div>
                  <div className="text-lg font-semibold text-green-700 mb-1">All Caught Up!</div>
                  <p className="text-sm text-green-600">No leads currently need follow-up. Great work!</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white p-1 shadow-sm" data-testid="leads-view-toggle">
          <button
            onClick={() => changeViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            data-testid="leads-view-list"
          >
            <ListIcon className="h-4 w-4" /> List
          </button>
          <button
            onClick={() => changeViewMode('kanban')}
            className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'kanban' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            data-testid="leads-view-kanban"
          >
            <LayoutGrid className="h-4 w-4" /> Kanban
            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-orange-500 text-white" data-testid="kanban-new-badge">
              New
            </span>
          </button>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={() => setShowAddLead(true)}
            className="bg-[#1F8A7C] hover:bg-[#18705F] text-white"
            data-testid="add-lead-button"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
          <Button
            onClick={() => navigate('/follow-up-queue')}
            variant="outline" className="bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
            data-testid="follow-up-queue-button"
          >
            <ListTodo className="h-4 w-4 mr-2" />
            Follow-Up Queue
          </Button>
          <Button
            onClick={handleSyncEmails}
            disabled={syncing}
            variant="outline" className="bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? "Syncing..." : "Sync Emails"}
          </Button>
          <Button
            onClick={exportToCSV}
            variant="outline" className="bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
            data-testid="export-csv-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <div className="mt-2 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
              data-testid="kanban-search-input"
            />
          </div>
          <p className="text-xs text-gray-500">Drag a card between columns to update its stage. Logging 5 outreach attempts (Call/Email/SMS) auto-closes a lead as "No Response".</p>
          <LeadsKanban
            leads={leads}
            searchTerm={searchTerm}
            onStatusChange={updateLeadStatus}
            onCardClick={(id) => navigate(`/leads/${id}?from=kanban`)}
          />
        </div>
      ) : (
      /* Tabs for Open / Qualified / Converted / Existing / All - Updated workflow */
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-full grid grid-cols-4 sm:grid-cols-7 gap-1 h-auto p-1">
          <TabsTrigger value="open" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-white">
            <AlertCircle className="h-3 w-3 flex-shrink-0 text-blue-600" />
            <span className="hidden sm:inline">Open</span>
            <span className="text-xs bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
              {leads.filter(l => ['new', 'contacted'].includes(l.status)).length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="existing" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-white">
            <Users className="h-3 w-3 flex-shrink-0 text-purple-600" />
            <span className="hidden sm:inline">Existing</span>
            <span className="text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
              {leads.filter(l => l.status === 'existing_customer').length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="qualified" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-white">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-amber-600" />
            <span className="hidden sm:inline">Qualified</span>
            <span className="text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
              {leads.filter(l => l.status === 'qualified').length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-white">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-emerald-600" />
            <span className="hidden sm:inline">Converted</span>
            <span className="text-xs bg-gradient-to-r from-emerald-500 to-green-500 text-white px-2 py-0.5 rounded-full flex-shrink-0 font-medium">{completedLeads.length}</span>
          </TabsTrigger>
          <TabsTrigger value="lost" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-white">
            <Trash2 className="h-3 w-3 flex-shrink-0 text-red-600" />
            <span className="hidden sm:inline">Lost</span>
            <span className="text-xs bg-gradient-to-r from-red-500 to-rose-500 text-white px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
              {leads.filter(l => ['lost', 'not_a_lead', 'closed_no_response'].includes(l.status)).length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-white">
            <span className="hidden sm:inline">All</span>
            <span className="text-xs bg-gradient-to-r from-gray-500 to-slate-500 text-white px-2 py-0.5 rounded-full flex-shrink-0 font-medium">{leads.length}</span>
          </TabsTrigger>
          <TabsTrigger value="rep-activity" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-white">
            <BarChart3 className="h-3 w-3 flex-shrink-0 text-purple-600" />
            <span className="hidden sm:inline">Rep Activity</span>
          </TabsTrigger>
        </TabsList>

        {/* Active Service Filter Indicator */}
        {serviceFilter && (
          <div className="mt-4 flex items-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
            <Filter className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">
              Filtering by service: <strong>{serviceFilter}</strong>
            </span>
            <span className="text-xs text-gray-500">({filteredLeads.length} leads)</span>
            <button
              onClick={() => {
                setServiceFilter(null);
                toast.info("Service filter cleared");
              }}
              className="ml-auto px-3 py-1 text-xs bg-white border border-blue-300 rounded hover:bg-blue-50 text-blue-700 font-medium"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Active Urgency Filter Indicator */}
        {urgencyFilter !== "all" && (
          <div className="mt-4 flex items-center gap-2 p-3 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg">
            <Bell className="h-4 w-4 text-orange-600" />
            <span className="text-sm font-medium text-gray-700">
              Filtering by urgency: <strong className="capitalize">{urgencyFilter}</strong>
            </span>
            <span className="text-xs text-gray-500">({filteredLeads.length} leads)</span>
            <button
              onClick={() => {
                setUrgencyFilter("all");
                toast.info("Urgency filter cleared");
              }}
              className="ml-auto px-3 py-1 text-xs bg-white border border-orange-300 rounded hover:bg-orange-50 text-orange-700 font-medium"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Main Leads Content - shown for all tabs except rep-activity */}
        {activeTab !== 'rep-activity' && (
          <div className="mt-6 space-y-6">
          {/* Filters */}
      <Card className="card p-6" data-testid="sort-controls">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
              <SelectItem value="not_a_lead">Not a Lead</SelectItem>
              <SelectItem value="closed_no_response">Closed – No Response</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-11" data-testid="source-filter">
              <SelectValue placeholder="Filter by source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="outlook">Outlook</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2">
            <Select value={sortBy} onValueChange={(val) => { setSortBy(val); if (val === "default") setSortOrder("desc"); }}>
              <SelectTrigger className="h-11" data-testid="sort-by-select">
                <div className="flex items-center gap-2">
                  <ArrowUpDown className="h-4 w-4 text-gray-400 shrink-0" />
                  <SelectValue placeholder="Sort" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default Sort</SelectItem>
                <SelectItem value="activity_date">Last Activity</SelectItem>
                <SelectItem value="created_date">Created Date</SelectItem>
                <SelectItem value="age">Age (days since created)</SelectItem>
                <SelectItem value="contacts">Contact Count</SelectItem>
                <SelectItem value="score">Lead Score</SelectItem>
                <SelectItem value="revenue">Revenue</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>
            {sortBy !== "default" && (
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 shrink-0"
                onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
                data-testid="sort-order-toggle"
                title={sortOrder === "asc" ? "Low → High" : "High → Low"}
              >
                {sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
              </Button>
            )}
          </div>
          {/* Contact count filter */}
          <Select
            value={contactedFilter}
            onValueChange={(v) => { setContactedFilter(v); localStorage.setItem("leads_contacted_filter", v); }}
          >
            <SelectTrigger className="h-11" data-testid="contacted-filter">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400 shrink-0" />
                <SelectValue placeholder="Contacts" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Contacts</SelectItem>
              <SelectItem value="none">Never Contacted</SelectItem>
              <SelectItem value="3plus">Contacted 3+ times</SelectItem>
            </SelectContent>
          </Select>
          {/* Location Filter - Only visible for master admins (users with no location_id) */}
          {currentUser && !currentUser.location_id && (
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="h-11" data-testid="location-filter">
                <SelectValue placeholder="Filter by location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </Card>

      {/* Date Range Filter & Statistics */}
      <Card className="card p-4">
        <div className="flex flex-col gap-4">
          {/* Date inputs row - responsive */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-11 w-full"
              />
            </div>
            <div className="w-full">
              <label className="text-sm font-medium text-gray-700 mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-11 w-full"
              />
            </div>
          </div>
          
          {/* Buttons row - responsive */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              onClick={fetchDateRangeStats}
              className="h-11 w-full sm:w-auto bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
              disabled={!startDate || !endDate}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Show Stats
            </Button>
            {showDateStats && (
              <Button 
                onClick={clearDateFilter}
                variant="outline"
                className="h-11 w-full sm:w-auto"
              >
                Clear Filter
              </Button>
            )}
          </div>
        </div>

        {/* Date Range Statistics */}
        {dateRangeStats && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              Period: {new Date(dateRangeStats.start_date).toLocaleDateString()} - {new Date(dateRangeStats.end_date).toLocaleDateString()}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-4 rounded-xl shadow-lg">
                <div className="text-sm font-medium text-blue-100 mb-1">Total Leads</div>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {dateRangeStats.total_leads}
                </div>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-green-600 text-white p-4 rounded-xl shadow-lg">
                <div className="text-sm font-medium text-emerald-100 mb-1">Converted</div>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {dateRangeStats.converted_leads}
                </div>
                <div className="text-xs text-emerald-100 mt-1">
                  {dateRangeStats.conversion_rate}% conversion rate
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white p-4 rounded-xl shadow-lg">
                <div className="text-sm font-medium text-purple-100 mb-1">Conversion Value</div>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  ${dateRangeStats.total_revenue.toLocaleString()}
                </div>
              </div>
              <div className="bg-gradient-to-br from-gray-500 to-slate-600 text-white p-4 rounded-xl shadow-lg">
                <div className="text-sm font-medium text-gray-100 mb-1">Status Breakdown</div>
                <div className="space-y-1 text-sm">
                  {Object.entries(dateRangeStats.status_breakdown).map(([status, count]) => (
                    <div key={status} className="flex justify-between">
                      <span className="capitalize text-gray-200">{status}:</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Leads List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="text-gray-600">Loading leads...</div>
        </div>
      ) : filteredLeads.length === 0 ? (
        <Card className="card p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Users className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>No leads found</h3>
          <p className="text-gray-600">Try adjusting your filters or connect your Outlook account</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {canBulkDelete && <LeadsBulkDelete visible={sortedLeads} selected={selectedIds} setSelected={setSelectedIds} onDeleted={() => { fetchLeads(); fetchCompletedLeads(); }} />}
          {sortedLeads.map((lead) => (
            <Card
              key={lead.id}
              className="card p-6 cursor-pointer hover:shadow-xl"
              onClick={() => {
                try {
                  sessionStorage.setItem(
                    'leads_nav_list',
                    JSON.stringify({
                      ids: sortedLeads.map((l) => l.id),
                      tab: activeTab,
                    })
                  );
                } catch (e) {}
                navigate(`/leads/${lead.id}?from=list`);
              }}
              data-testid={`lead-card-${lead.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    {canBulkDelete && (
                      <span onClick={(e) => e.stopPropagation()} className="flex items-center">
                        <Checkbox checked={selectedIds.has(lead.id)} data-testid={`lead-select-${lead.id}`}
                          onCheckedChange={(v) => setSelectedIds((s) => { const n = new Set(s); v ? n.add(lead.id) : n.delete(lead.id); return n; })} />
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
                      {lead.name || "Unknown Lead"}
                    </h3>
                    {lead.self_registered && <SelfRegisteredBadge size="md" withPulse />}
                    {/* Urgency Badge */}
                    {lead.follow_up && getUrgencyBadge(lead.follow_up)}
                    {/* Not a Lead Badge */}
                    {lead.status === 'not_a_lead' && (
                      <div
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm bg-gradient-to-r from-amber-500 to-yellow-600"
                        data-testid={`not-a-lead-badge-${lead.id}`}
                        title={`Excluded from conversion rate${lead.not_a_lead_reason ? ` · ${lead.not_a_lead_reason.replace(/_/g, ' ')}` : ''}`}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                        NOT A LEAD
                        {lead.not_a_lead_reason && (
                          <span className="ml-1 opacity-90 capitalize">
                            · {lead.not_a_lead_reason.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Status Selector */}
                    <div onClick={(e) => e.stopPropagation()}>
                      <Select 
                        value={lead.status} 
                        onValueChange={async (newStatus) => {
                          try {
                            const payload = { status: newStatus };
                            if (newStatus === 'not_a_lead') {
                              payload.not_a_lead_reason = lead.not_a_lead_reason || 'general_inquiry';
                            }
                            await axios.patch(`${API}/leads/${lead.id}/status`, 
                              payload,
                              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
                            );
                            toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
                            fetchLeads();
                            fetchCompletedLeads();
                          } catch (error) {
                            toast.error('Failed to update status');
                          }
                        }}
                      >
                        <SelectTrigger className={`w-32 h-7 text-xs status-badge status-${lead.status} border-0`}>
                          <SelectValue>{lead.status === 'not_a_lead' ? 'not a lead' : lead.status}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="existing_customer">Existing Customer</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                          <SelectItem value="not_a_lead">Not a Lead</SelectItem>
                          <SelectItem value="closed_no_response">Closed – No Response</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {leadTags[lead.id]?.length > 0 && <div data-testid={`lead-list-tags-${lead.id}`}><ProfileTags tags={leadTags[lead.id]} /></div>}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    {lead.canonical_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {lead.canonical_email}
                      </div>
                    )}
                    {lead.primary_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        {formatPhoneNumber(lead.primary_phone)}
                      </div>
                    )}
                    {lead.company && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {lead.company}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  {/* Show Revenue for Converted Leads */}
                  {activeTab === "completed" && posMatches[lead.id]?.pos_payload?.total_revenue && (
                    <div className="text-right">
                      <div className="text-sm text-gray-500 mb-1">Total Revenue</div>
                      <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Manrope' }}>
                        ${parseFloat(posMatches[lead.id].pos_payload.total_revenue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      {posMatches[lead.id]?.pos_payload?.deposits_collected && parseFloat(posMatches[lead.id].pos_payload.deposits_collected) > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          Incl. ${parseFloat(posMatches[lead.id].pos_payload.deposits_collected).toFixed(2)} deposit
                        </div>
                      )}
                    </div>
                  )}
                  <div className="text-right">
                    <div className="text-sm text-gray-500 mb-1">Lead Score</div>
                    <div className="text-2xl font-bold text-cyan-600" style={{ fontFamily: 'Manrope' }}>{lead.score}</div>
                  </div>
                  {/* Last Action Column */}
                  <div className="text-right min-w-[100px]">
                    <div className="text-sm text-gray-500 mb-1">Last Action</div>
                    {(() => {
                      const lastAction = getLastAction(lead);
                      const formatted = formatLastAction(lastAction);
                      const ActionIcon = formatted.icon;
                      return (
                        <div className={`flex items-center justify-end gap-2 text-sm ${formatted.isStale ? 'text-red-500 font-semibold' : formatted.color}`}>
                          <ActionIcon className="h-4 w-4" />
                          <span>{formatted.text}</span>
                        </div>
                      );
                    })()}
                    {(() => {
                      const lastAction = getLastAction(lead);
                      const formatted = formatLastAction(lastAction);
                      return formatted.by ? (
                        <div className="text-xs text-gray-400 mt-0.5">by {formatted.by}</div>
                      ) : null;
                    })()}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500 mb-1">Created</div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Calendar className="h-4 w-4" />
                      {new Date(lead.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteLead(lead.id, lead.name || "this lead", e)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
        </div>
        )}
        
        {/* Rep Activity Tab */}
        {activeTab === 'rep-activity' && (
          <div className="mt-6 space-y-6">
          <Card className="border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                Rep Performance Report
              </h3>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <Input
                    type="date"
                    value={repStartDate}
                    onChange={(e) => setRepStartDate(e.target.value)}
                    className="w-40"
                    data-testid="rep-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <Input
                    type="date"
                    value={repEndDate}
                    onChange={(e) => setRepEndDate(e.target.value)}
                    className="w-40"
                    data-testid="rep-end-date"
                  />
                </div>
                <Button 
                  onClick={fetchRepActivity}
                  disabled={repActivityLoading}
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                  data-testid="fetch-rep-activity-btn"
                >
                  {repActivityLoading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <BarChart3 className="h-4 w-4 mr-2" />
                  )}
                  Generate Report
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Rep Stats Summary */}
          {repActivityStats && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 shadow-lg">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm font-medium">Total Reps Active</p>
                        <p className="text-4xl font-bold mt-1">{repActivityStats.total_reps}</p>
                      </div>
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white border-0 shadow-lg">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-cyan-100 text-sm font-medium">Total Actions</p>
                        <p className="text-4xl font-bold mt-1">{repActivityStats.total_actions}</p>
                      </div>
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white border-0 shadow-lg">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-emerald-100 text-sm font-medium">Date Range</p>
                        <p className="text-lg font-bold mt-1">
                          {new Date(repActivityStats.start_date).toLocaleDateString()} - {new Date(repActivityStats.end_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <Calendar className="w-6 h-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Rep Performance Table */}
              <Card>
                <CardContent className="p-0">
                  <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Click on a rep row to filter their actions below
                    </p>
                    {selectedRepFilter && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedRepFilter(null)}
                        className="text-purple-600 hover:text-purple-700"
                      >
                        Show All Reps
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="rep-activity-table">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
                        <tr>
                          <th className="text-left py-4 px-6 font-semibold text-gray-700">Rep Name</th>
                          <th className="text-center py-4 px-4 font-semibold text-gray-700">
                            <div className="flex items-center justify-center gap-1">
                              <Phone className="h-4 w-4 text-blue-600" />
                              Calls
                            </div>
                          </th>
                          <th className="text-center py-4 px-4 font-semibold text-gray-700">
                            <div className="flex items-center justify-center gap-1">
                              <Mail className="h-4 w-4 text-green-600" />
                              Emails
                            </div>
                          </th>
                          <th className="text-center py-4 px-4 font-semibold text-gray-700">Comments</th>
                          <th className="text-center py-4 px-4 font-semibold text-gray-700">Other</th>
                          <th className="text-center py-4 px-4 font-semibold text-gray-700 bg-purple-50">
                            <span className="text-purple-700">Total Actions</span>
                          </th>
                          <th className="text-center py-4 px-4 font-semibold text-gray-700">Leads Worked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repActivityStats.reps.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-8 text-gray-500">
                              No activity found in this date range
                            </td>
                          </tr>
                        ) : (
                          repActivityStats.reps.map((rep, idx) => (
                            <tr 
                              key={rep.rep_email}
                              onClick={() => setSelectedRepFilter(selectedRepFilter === rep.rep_email ? null : rep.rep_email)}
                              className={`border-b transition-colors cursor-pointer ${
                                selectedRepFilter === rep.rep_email 
                                  ? 'bg-gradient-to-r from-purple-100 to-indigo-100 ring-2 ring-purple-400' 
                                  : idx === 0 
                                    ? 'bg-gradient-to-r from-yellow-50 to-amber-50 hover:from-yellow-100 hover:to-amber-100' 
                                    : 'hover:bg-gray-50'
                              }`}
                            >
                              <td className="py-4 px-6">
                                <div className="flex items-center gap-3">
                                  {idx === 0 && !selectedRepFilter && (
                                    <span className="text-xl">🏆</span>
                                  )}
                                  {selectedRepFilter === rep.rep_email && (
                                    <span className="text-xl">👤</span>
                                  )}
                                  <div>
                                    <div className="font-medium text-gray-900">{rep.rep_name}</div>
                                    <div className="text-xs text-gray-500">{rep.rep_email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center py-4 px-4">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-semibold">
                                  {rep.calls}
                                </span>
                              </td>
                              <td className="text-center py-4 px-4">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-700 font-semibold">
                                  {rep.emails}
                                </span>
                              </td>
                              <td className="text-center py-4 px-4">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-700 font-semibold">
                                  {rep.comments}
                                </span>
                              </td>
                              <td className="text-center py-4 px-4">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-700 font-semibold">
                                  {rep.other}
                                </span>
                              </td>
                              <td className="text-center py-4 px-4 bg-purple-50">
                                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-600 text-white font-bold text-lg">
                                  {rep.total_actions}
                                </span>
                              </td>
                              <td className="text-center py-4 px-4">
                                <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 font-semibold">
                                  {rep.leads_worked} leads
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Actions List */}
              {repActivityStats.recent_actions && repActivityStats.recent_actions.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>
                        {selectedRepFilter 
                          ? `Actions by ${repActivityStats.reps.find(r => r.rep_email === selectedRepFilter)?.rep_name || 'Selected Rep'}`
                          : 'Recent Actions (All Reps)'}
                      </h4>
                      {selectedRepFilter && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedRepFilter(null)}
                          className="text-purple-600 border-purple-300 hover:bg-purple-50"
                        >
                          <X className="w-4 h-4 mr-1" /> Clear Filter
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {repActivityStats.recent_actions
                        .filter(action => !selectedRepFilter || action.rep_email === selectedRepFilter)
                        .sort((a, b) => {
                          // Sort by rep name first, then by date (newest first)
                          if (a.rep_name !== b.rep_name) {
                            return a.rep_name.localeCompare(b.rep_name);
                          }
                          return new Date(b.action_date) - new Date(a.action_date);
                        })
                        .map((action, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => navigate(`/leads/${action.lead_id}`)}
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                            {action.action_type.includes('call') ? (
                              <Phone className="w-5 h-5 text-blue-600" />
                            ) : action.action_type.includes('email') ? (
                              <Mail className="w-5 h-5 text-green-600" />
                            ) : (
                              <Info className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{action.rep_name}</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-sm text-purple-600 capitalize">{action.action_type.replace('_', ' ')}</span>
                            </div>
                            <div className="text-sm text-gray-600 truncate">
                              Lead: {action.lead_name}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 flex-shrink-0">
                            {new Date(action.action_date).toLocaleDateString()} {new Date(action.action_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Empty State */}
          {!repActivityStats && !repActivityLoading && (
            <Card className="p-12">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                  <BarChart3 className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Generate a Report</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Select a date range above and click "Generate Report" to see rep activity and accountability metrics.
                </p>
              </div>
            </Card>
          )}
        </div>
        )}
      </Tabs>
      )}

      {/* Add Lead Dialog */}
      <Dialog open={showAddLead} onOpenChange={setShowAddLead}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <UserPlus className="h-5 w-5 text-amber-600" />
              Add Lead Manually
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLead} className="space-y-4 mt-2" data-testid="add-lead-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="lead-name" className="text-xs font-medium text-gray-600">Owner Name *</Label>
                <input id="lead-name" data-testid="add-lead-name" required className="w-full mt-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none" placeholder="First Last" value={addLeadForm.name} onChange={e => setAddLeadForm(f => ({...f, name: e.target.value}))} />
              </div>
              <div>
                <Label htmlFor="lead-email" className="text-xs font-medium text-gray-600">Email</Label>
                <input id="lead-email" data-testid="add-lead-email" type="email" className="w-full mt-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none" placeholder="email@example.com" value={addLeadForm.email} onChange={e => setAddLeadForm(f => ({...f, email: e.target.value}))} />
              </div>
              <div>
                <Label htmlFor="lead-phone" className="text-xs font-medium text-gray-600">Phone</Label>
                <input id="lead-phone" data-testid="add-lead-phone" type="tel" className="w-full mt-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none" placeholder="(555) 123-4567" value={addLeadForm.phone} onChange={e => setAddLeadForm(f => ({...f, phone: e.target.value}))} />
              </div>
              <div>
                <Label htmlFor="lead-service" className="text-xs font-medium text-gray-600">Service Interested In</Label>
                <select id="lead-service" data-testid="add-lead-service" className="w-full mt-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none bg-white" value={addLeadForm.service} onChange={e => setAddLeadForm(f => ({...f, service: e.target.value}))}>
                  <option value="">Select...</option>
                  <option value="Dog Boarding">Dog Boarding</option>
                  <option value="Dog Daycare">Dog Daycare</option>
                  <option value="Both Boarding and Daycare">Both</option>
                  <option value="Schedule A Tour">Schedule a Tour</option>
                </select>
              </div>
              <div>
                <Label htmlFor="lead-source" className="text-xs font-medium text-gray-600">Source</Label>
                <select id="lead-source" data-testid="add-lead-source" className="w-full mt-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none bg-white" value={addLeadForm.source} onChange={e => setAddLeadForm(f => ({...f, source: e.target.value}))}>
                  <option value="event">Event</option>
                  <option value="walk_in">Walk-In</option>
                  <option value="phone_call">Phone Call</option>
                  <option value="referral">Referral</option>
                  <option value="manual">Other</option>
                </select>
              </div>
              <div>
                <Label htmlFor="lead-dog-name" className="text-xs font-medium text-gray-600">Dog Name</Label>
                <input id="lead-dog-name" data-testid="add-lead-dog-name" className="w-full mt-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none" placeholder="Buddy" value={addLeadForm.dog_name} onChange={e => setAddLeadForm(f => ({...f, dog_name: e.target.value}))} />
              </div>
              <div>
                <Label htmlFor="lead-dog-breed" className="text-xs font-medium text-gray-600">Dog Breed</Label>
                <input id="lead-dog-breed" data-testid="add-lead-dog-breed" className="w-full mt-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none" placeholder="Golden Retriever" value={addLeadForm.dog_breed} onChange={e => setAddLeadForm(f => ({...f, dog_breed: e.target.value}))} />
              </div>
              <div className="col-span-2">
                <Label htmlFor="lead-notes" className="text-xs font-medium text-gray-600">Notes</Label>
                <Textarea id="lead-notes" data-testid="add-lead-notes" className="w-full mt-1 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" placeholder="Any additional notes from the event form..." rows={2} value={addLeadForm.notes} onChange={e => setAddLeadForm(f => ({...f, notes: e.target.value}))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddLead(false)}>Cancel</Button>
              <Button type="submit" disabled={addingLead} className="bg-amber-600 hover:bg-amber-700" data-testid="add-lead-submit">
                {addingLead ? "Adding..." : "Add Lead"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Leads;
